import type { FC } from 'react';
import React, { useContext } from 'react';
import { cssMap, styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { Inline, Stack, Box, xcss } from '@atlaskit/primitives';

import { AISnippetWithRelayEnvironment } from '@atlassian/ai-snippet';

import { fg } from '@confluence/feature-gating';
import { FeatureCode } from '@confluence/feature-codes';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';
import { useSessionData } from '@confluence/session-data';

import { PageCardContext } from '../PageCardContext';

import {
	AISnippetFooter,
	AutomationDiscoveryMessage,
	ContentIcon,
	ExcerptWithAISnippet,
	ExcerptWithAISnippetV2,
	LastAction,
	PageCardContainer,
	PageLink,
	QuickActions,
	SocialProof,
	StatusLozenge,
	Subtitle,
	Title,
} from '../Items';

const styles = xcss({
	width: '400px',
});
const headerStyle = xcss({
	width: '100%',
	overflowWrap: 'break-word',
	wordBreak: 'break-all',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
const headerIconStyle = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignSelf: 'flex-start',
});

const titleStyles = xcss({
	font: token('font.heading.medium'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	overflowWrap: 'break-word',
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
});

const aiSnippetStyles = cssMap({
	container: {
		minHeight: '96px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		pre: {
			// We need this so code wraps in the preview, without it the code will overflow the card
			whiteSpace: 'pre-wrap',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const PopupCard = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '400px',
	padding: `${token('space.150')} ${token('space.200')} ${token('space.200')} ${token('space.200')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Header = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const TitleContainer = styled.div({
	margin: `${token('space.200')} 0 ${token('space.050')} 0`,
	font: token('font.heading.medium'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	overflowWrap: 'break-word',
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
});

type PopupViewWithAISnippetProps = {
	shouldUseAutomationDiscovery?: boolean; // this should probably go into PageTreeContext, however since this is the only usage, it's ok to leave it here for now
	error?: boolean;
	loading?: boolean;
	snippet?: string;
	isEndOfPage?: boolean;
	contentId: string;
	contentType: string;
	onLoadComplete?: () => void;
};

export const PopupViewWithAISnippet: FC<PopupViewWithAISnippetProps> = ({
	shouldUseAutomationDiscovery,
	loading,
	snippet,
	isEndOfPage = false,
	contentId,
	contentType,
	onLoadComplete,
}) => {
	const isNav4Enabled = useIsNav4Enabled();
	if (fg('p2m-hover-card-updates')) {
		return (
			<PopupViewWithAISnippetV2
				shouldUseAutomationDiscovery={shouldUseAutomationDiscovery}
				loading={loading}
				snippet={snippet}
				isEndOfPage={isEndOfPage}
				contentId={contentId}
				contentType={contentType}
				onLoadComplete={onLoadComplete}
			/>
		);
	}
	return (
		<PageCardContainer isHoverPageCard>
			<PageLink
				shouldHideFolder
				featureAttributionCode={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
			/>
			<PopupCard data-testid="page-card-popup-view">
				<Header>
					<ContentIcon />
					<QuickActions actions={['edit', 'star']} />
				</Header>
				<TitleContainer>
					<Title />
				</TitleContainer>
				<Stack {...(isNav4Enabled && { space: 'space.050' })}>
					<Subtitle />
					<Inline alignBlock="center">
						<LastAction />
						<SocialProof isInlineAction />
					</Inline>
				</Stack>
				<StatusLozenge isInColumn />
				<ExcerptWithAISnippet loading={!!loading} snippet={snippet} isEndOfPage={isEndOfPage} />
			</PopupCard>
			{!loading && <AISnippetFooter showAIFooter={!!snippet} />}
			<AutomationDiscoveryMessage shouldUseAutomationDiscovery={shouldUseAutomationDiscovery} />
		</PageCardContainer>
	);
};

export const PopupViewWithAISnippetV2: FC<PopupViewWithAISnippetProps> = ({
	shouldUseAutomationDiscovery,
	loading,
	snippet,
	isEndOfPage = false,
	contentId,
	contentType,
	onLoadComplete,
}) => {
	const { cloudId } = useSessionData();
	const { excerpt } = useContext(PageCardContext);

	return (
		<PageCardContainer isHoverPageCard>
			<PageLink
				shouldHideFolder
				featureAttributionCode={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
			/>
			<Box padding="space.200" xcss={styles}>
				<Stack alignBlock="start" alignInline="start" grow="fill" space="space.200">
					<Inline
						alignBlock="center"
						alignInline="start"
						shouldWrap={false}
						grow="fill"
						space="space.100"
					>
						<Box xcss={headerIconStyle}>
							<ContentIcon />
						</Box>
						<Stack xcss={headerStyle}>
							<Box xcss={titleStyles}>
								<Title />
							</Box>
							<Subtitle />
						</Stack>
					</Inline>
					{cloudId && fg('use-aisnippets-platform-component') ? (
						<Box xcss={aiSnippetStyles.container}>
							<AISnippetWithRelayEnvironment
								contentId={contentId}
								contentType={contentType}
								cloudId={cloudId}
								source={isEndOfPage ? 'endOfPageRecommendations' : 'pageTree'}
								onLoadComplete={onLoadComplete}
								fallbackText={excerpt}
							/>
						</Box>
					) : (
						<ExcerptWithAISnippetV2
							loading={!!loading}
							snippet={snippet}
							isEndOfPage={isEndOfPage}
						/>
					)}
					<Inline
						alignBlock="center"
						spread="space-between"
						shouldWrap={false}
						grow="fill"
						space="space.200"
					>
						<LastAction />
						<SocialProof isInlineAction />
					</Inline>
				</Stack>
			</Box>
			<AutomationDiscoveryMessage shouldUseAutomationDiscovery={shouldUseAutomationDiscovery} />
		</PageCardContainer>
	);
};
